import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { FaUsers, FaUserShield, FaChartLine, FaCog, FaEdit, FaTrash, FaPlus, FaEye, FaDollarSign } from 'react-icons/fa';
import api from '../../services/api';

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: '',
    status: 'active'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [usersData,statsData] = await Promise.all([
        api.getUsers(1, 50),
        api.getSuperAdminDashboard()
      ]);
      setUsers(usersData.data || []);
      setStats(statsData.data || {});
    } catch (error) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleUserEdit = (user) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status || 'active'
    });
    setShowUserModal(true);
  };

  const handleUserUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateUserRole(selectedUser.id, userForm.role);
      setSuccess('User role updated successfully');
      setShowUserModal(false);
      loadDashboardData();
    } catch (error) {
      setError('Failed to update user role');
    }
  };

  const handleUserDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.deleteUser(userId);
        setSuccess('User deleted successfully');
        loadDashboardData();
      } catch (error) {
        setError('Failed to delete user');
      }
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      'super-admin': 'danger',
      'admin': 'warning',
      'doctor': 'info',
      'patient': 'primary',
      'driver': 'secondary'
    };
    return <Badge bg={roleColors[role] || 'secondary'}>{role}</Badge>;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'active': 'success',
      'inactive': 'secondary',
      'suspended': 'danger',
      'pending': 'warning'
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4" style={{ backgroundColor: '#f8fffe' }}>
      <Container fluid>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h2 fw-bold" style={{ color: '#0891b2' }}>
              <FaUserShield className="me-2" />
              Super Admin Dashboard
            </h1>
            <p className="text-muted mb-0">Complete system management and oversight</p>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        <Row className="g-4 mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                     style={{
                       width: '60px',
                       height: '60px',
                       background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)'
                     }}>
                  <FaUsers className="text-white" size={24} />
                </div>
                <h3 className="h4 fw-bold mb-1">{stats.totalPatients || 0}</h3>
                <p className="text-muted mb-0">Total Patients</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                     style={{
                       width: '60px',
                       height: '60px',
                       background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                     }}>
                  <FaUserShield className="text-white" size={24} />
                </div>
                <h3 className="h4 fw-bold mb-1">{stats.totalAdmins || 0}</h3>
                <p className="text-muted mb-0">Administrators</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                     style={{
                       width: '60px',
                       height: '60px',
                       background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                     }}>
                  <FaChartLine className="text-white" size={24} />
                </div>
                <h3 className="h4 fw-bold mb-1">{stats.totalAppointments || 0}</h3>
                <p className="text-muted mb-0">Appointments</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                     style={{
                       width: '60px',
                       height: '60px',
                       background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                     }}>
                  <FaDollarSign className="text-white" size={24} />
                </div>
                <h3 className="h4 fw-bold mb-1">PKR {stats.totalRevenue }</h3>
                <p className="text-muted mb-0">Total Revenue</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Users Management */}
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white border-0 py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold">User Management</h5>
              {/* <Button
                size="sm"
                style={{
                  background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                  border: "none"
                }}
              >
                <FaPlus className="me-2" />
                Add User
              </Button> */}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0">User</th>
                    <th className="border-0">Role</th>
                    <th className="border-0">Status</th>
                    <th className="border-0">Last Active</th>
                    <th className="border-0">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                               style={{
                                 width: '40px',
                                 height: '40px',
                                 background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)'
                               }}>
                            <FaUsers className="text-white" size={16} />
                          </div>
                          <div>
                            <div className="fw-bold">{user.name}</div>
                            <small className="text-muted">{user.email}</small>
                          </div>
                        </div>
                      </td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>{getStatusBadge(user.status)}</td>
                      <td>
                        <small className="text-muted">
                          {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : 'Never'}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleUserEdit(user)}
                          >
                            <FaEdit size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleUserDelete(user.id)}
                          >
                            <FaTrash size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* User Edit Modal */}
        <Modal show={showUserModal} onHide={() => setShowUserModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleUserUpdate}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={userForm.name}
                  onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                  disabled
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  disabled
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  value={userForm.role}
                  onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                >
                  <option value="patient">Patient</option>
                  <option value="doctor">Doctor</option>
                  <option value="admin">Admin</option>
                  <option value="driver">Driver</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={userForm.status}
                  onChange={(e) => setUserForm({...userForm, status: e.target.value})}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUserUpdate}
              style={{
                background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                border: "none"
              }}
            >
              Update User
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
